import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Call, CallStatus } from "../../../../core/models/call";
import { createRoute, ROUTES } from "../../../../core/constants/app";
import { imageList } from "@/core/utils/imageList";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import "./CallCard.scss";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { formatRelativeTime } from "@/core/utils/format";
import { Tags } from "@/core/components/tags/Tags";
import { updateCallBookmark } from "../../store";
import { $userBasicInfo } from "@/features/user/store";
import { useUnit } from "effector-react";

interface CallCardProps {
  call: Call;
  showPartners?: boolean;
}

const CallCard: React.FC<CallCardProps> = ({ call, showPartners }) => {
  const [isClicked, setIsClicked] = useState<boolean>(false);
  const [userBasicInfo] = useUnit([$userBasicInfo]);
  const { pro_user } = userBasicInfo || {};

  const handleBookmarkAction = async (
    callId: string,
    isBookmarked: boolean,
  ) => {
    // The effect handles the API call, just trigger the event
    updateCallBookmark({ callId, isBookmarked: !isBookmarked });
  };

  const formatDeadline = (deadline: string | undefined): string => {
    try {
      if (!deadline) return "No deadline";
      const date = new Date(deadline);
      if (isNaN(date.getTime())) return "Invalid date";
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  // Early return for invalid call data
  if (!call || !call.id) {
    return (
      <div className="call-card call-card--error">
        <div className="call-card__error">
          <p>Invalid call data</p>
        </div>
      </div>
    );
  }

  // Safe data extraction with defaults
  const safeCall = {
    id: call.id,
    title: call.title || "Untitled Call",
    amount: call.amount || 0,
    deadline: call.deadline || "",
    categories: Array.isArray(call.categories) ? call.categories : [],
    comments: typeof call.comments === "number" ? call.comments : 0,
    ideas: typeof call.ideas === "number" ? call.ideas : 0,
    max_score: call.max_score,
  };
  const getDaysLeftInfo = (deadline: string) => {
    try {
      if (!deadline)
        return { daysLeft: 0, cssClass: "badgeYellow", text: "No deadline" };

      const now = new Date();
      const targetDate = new Date(deadline);

      if (isNaN(targetDate.getTime())) {
        return { daysLeft: 0, cssClass: "badgeYellow", text: "Invalid date" };
      }

      const diffTime = targetDate.getTime() - now.getTime();
      const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const cssClass = daysLeft > 39 ? "badgeGreen" : "badgeYellow";
      const text = formatRelativeTime(deadline);

      return { daysLeft, cssClass, text };
    } catch (error) {
      console.warn("Days calculation error:", error);
      return { daysLeft: 0, cssClass: "badgeYellow", text: "Error" };
    }
  };
  const daysInfo = getDaysLeftInfo(call.deadline);

  useEffect(() => {
    setIsClicked(call.is_call_bookmarked);
  }, [call.is_call_bookmarked]);

  return (
    <div className="call-card">
      <div className="call-header">
        <div className="call-type">
          <div className="eu-flag">
            {call.programme && call.programme.logo ? (
              <img
                src={call.programme.logo}
                alt={call.programme.name}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const placeholder = target.nextElementSibling as HTMLElement;
                  if (placeholder) placeholder.style.display = "flex";
                }}
              />
            ) : (
              <div
                className="eu-flag"
                style={{
                  display: imageList.samplePartner ? "none" : "flex",
                }}
              ></div>
            )}
          </div>
          <span>{call.programme?.name || "?"}</span>
        </div>
        <button
          className={`bookmark-btn ${isClicked ? "bookmarked" : ""} ${
            call.is_call_declared_interest ? "declared-interest" : ""
          }`}
          onClick={() => {
            if (!call.is_call_declared_interest) {
              handleBookmarkAction(call.id, isClicked);
              setIsClicked(!isClicked);
            }
          }}
          disabled={call.is_call_declared_interest}
        >
          <SvgIcon
            icon={
              call.is_call_declared_interest
                ? "icon-declare"
                : isClicked
                  ? "icon-bookmarked"
                  : "icon-bookmark"
            }
            className={call.is_call_declared_interest ? "declare-icon" : ""}
          />
        </button>
      </div>

      <div className="call-content">
        <div className="call-badges">
          {call.is_new && <span className="badge new-badge">New</span>}
          {daysInfo.daysLeft < 40 && (
            <Tags className={"badgeRed"} type="custom" text={"Expiring soon"} />
          )}
          <span className="badge deadline-badge">
            <SvgIcon icon="icon-clock" /> until{" "}
            {new Date(call.deadline).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
          <div className={`badge daysLeft ${daysInfo.cssClass}`}>
            <Tags
              className={daysInfo.cssClass}
              type="custom"
              text={daysInfo.text}
            />
          </div>
        </div>

        <h3 className="call-title">
          <Link to={createRoute(ROUTES.CALL_DETAIL, { id: call.id })}>
            {safeCall.title}
          </Link>
        </h3>

        <div className="call-budget">
          € {parseInt(safeCall.amount.toString()).toLocaleString()}
        </div>

        <div className="interested-partners">
          <span className="partners-label">Interested Partners</span>
          {!call?.interested_partners ||
            (call?.interested_partners.length === 0 && "0")}
          {typeof userBasicInfo?.partner === "string" && !showPartners && (
            <Tags type={"partner"} number={call?.interested_partners?.length} />
          )}
          {(typeof userBasicInfo?.partner === "object" || showPartners) &&
            call?.interested_partners &&
            call?.interested_partners?.length > 0 && (
              <div className="partners-avatars">
                {call?.interested_partners?.slice(0, 4).map((p, i) => (
                  <div
                    key={p.id || i}
                    className="partner-avatar-wrapper"
                    style={{
                      transform: i > 0 ? `translateX(-${30 * i}px)` : "none",
                      zIndex: i > 0 ? 2 + (i - 1) : 1,
                    }}
                  >
                    <div className="company-logo placeholder">
                      <BgImage
                        img={p.logo_uri}
                        altImage={imageList.noOrgimage}
                      />
                    </div>
                  </div>
                ))}

                {call.interested_partners.length > 4 && (
                  <span
                    className="more-count"
                    style={{
                      transform: `translateX(-${31 * call.interested_partners.slice(0, 4).length}px)`,
                      zIndex: 2 + call.interested_partners.length,
                    }}
                  >
                    +{call.interested_partners.length - 4}
                  </span>
                )}
              </div>
            )}
        </div>

        <div className="sectors-tags">
          {safeCall.categories.slice(0, 4).map((category, index) => (
            <span
              key={`${safeCall.id}-category-${index}`}
              className="sector-tag"
            >
              {category}
            </span>
          ))}
          {safeCall.categories.length > 4 && (
            <span className="sector-tag more-count">
              +{safeCall.categories.length - 4}
            </span>
          )}
        </div>
      </div>

      <div className="call-footer">
        {typeof safeCall?.max_score === "number" ? (
          <div
            className="match-badge"
            style={{
              backgroundColor:
                safeCall?.max_score >= 67
                  ? "green"
                  : safeCall?.max_score >= 34 && safeCall?.max_score < 66
                    ? "Orange"
                    : "#B93815",
            }}
          >{`${safeCall?.max_score}% match`}</div>
        ) : (
          <div></div>
        )}
        <div className="call-stats">
          <div className="stat-item item-comments">
            <SvgIcon icon="icon-message" /> {safeCall.comments} comments
          </div>
          <div className="stat-item item-ideas">
            <SvgIcon icon="icon-ideas" /> {safeCall.ideas} ideas
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallCard;
